import { supabase } from '../lib/supabase';

export interface GenerationMetric {
  document_type: string;
  tier: 'standard' | 'enhanced' | 'premium';
  generation_time_ms: number;
  cache_hit: boolean;
  token_count?: number;
  cost_estimate?: number;
  provider?: 'claude' | 'openai';
  retry_attempts?: number;
  failed_over?: boolean;
}

export const trackGenerationMetrics = async (
  metric: GenerationMetric
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('generation_metrics')
      .insert({
        document_type: metric.document_type,
        tier: metric.tier,
        generation_time_ms: metric.generation_time_ms,
        cache_hit: metric.cache_hit,
        token_count: metric.token_count,
        cost_estimate: metric.cost_estimate,
        provider: metric.provider,
        retry_attempts: metric.retry_attempts,
        failed_over: metric.failed_over,
      });

    if (error) {
      console.error('Error tracking metrics:', error);
    }
  } catch (error) {
    console.error('Error tracking metrics:', error);
  }
};

export const getAverageGenerationTime = async (
  documentType: string,
  tier: 'standard' | 'enhanced' | 'premium'
): Promise<number | null> => {
  try {
    const { data, error } = await supabase
      .from('generation_metrics')
      .select('generation_time_ms')
      .eq('document_type', documentType)
      .eq('tier', tier)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error || !data || data.length === 0) return null;

    const average = data.reduce((sum, m) => sum + m.generation_time_ms, 0) / data.length;
    return Math.round(average);
  } catch (error) {
    console.error('Error getting average generation time:', error);
    return null;
  }
};

export const getCacheHitRate = async (
  documentType?: string
): Promise<number | null> => {
  try {
    let query = supabase
      .from('generation_metrics')
      .select('cache_hit');

    if (documentType) {
      query = query.eq('document_type', documentType);
    }

    const { data, error } = await query
      .order('created_at', { ascending: false })
      .limit(100);

    if (error || !data || data.length === 0) return null;

    const hits = data.filter(m => m.cache_hit).length;
    const rate = (hits / data.length) * 100;
    return Math.round(rate);
  } catch (error) {
    console.error('Error getting cache hit rate:', error);
    return null;
  }
};

export const estimateTokenCount = (content: string): number => {
  return Math.ceil(content.length / 4);
};

export const estimateCost = (
  tokenCount: number,
  tier: 'standard' | 'enhanced' | 'premium'
): number => {
  const inputCostPer1M = 3.0;
  const outputCostPer1M = 15.0;

  const estimatedInputTokens = tokenCount * 0.3;
  const estimatedOutputTokens = tokenCount * 0.7;

  const inputCost = (estimatedInputTokens / 1000000) * inputCostPer1M;
  const outputCost = (estimatedOutputTokens / 1000000) * outputCostPer1M;

  return inputCost + outputCost;
};
